import { type DynamicColumn } from '@/lib/csv-box';
import {
  DatasourceInstructionMode,
  PricingMode,
} from '@/lib/graphql/generated/graphql';

/**
 * Aplatit les valeurs des colonnes dynamiques (renvoyées par CSVBox dans
 * `_dynamic_data`) au niveau racine de chaque ligne, pour qu'elles soient
 * incluses dans le CSV envoyé au backend (sinon elles seraient sérialisées
 * comme « [object Object] »).
 */
export function flattenDynamicData(rows: Record<string, unknown>[]) {
  return rows.map((row) => {
    const dynamic = row._dynamic_data as Record<string, unknown> | undefined;
    if (!dynamic) {
      return row;
    }
    const extended = { ...row, ...dynamic };
    delete extended._dynamic_data;
    return extended;
  });
}

/** Merge selected e-merch columns from _unmapped_data into each row. */
export function mergeUnmappedColumns(
  rows: Record<string, unknown>[],
  columns: string[]
) {
  return rows.map((row) => {
    const unmapped = row._unmapped_data as Record<string, unknown> | undefined;
    const extended = { ...row };
    delete extended._unmapped_data;
    for (const col of columns) {
      extended[col] = unmapped?.[col] ?? '';
    }
    return extended;
  });
}

/**
 * Colonnes à SAISIR exposées dynamiquement à CSVBox selon le mode de calcul
 * (cf. help.csvbox.io/advanced-installation/dynamic-columns).
 * Le `column_name` reprend le nom canonique des colonnes (cf. mapping.ts) :
 * c'est lui qui fait foi côté backend (pas de doublon des colonnes historiques).
 *
 * D'après les règles de dérivation backend, dans les DEUX modes le prix public
 * et le prix Choose sont saisis, et le discount est dérivé. Le discriminateur :
 * - MarginFixed      : on saisit la marge (discount + rétrocession dérivés).
 * - BuyingPriceFixed : on saisit la rétrocession (discount + marge dérivés).
 *
 * Nuance backend : `discount` ↔ `choosePrice` sont interchangeables. Le backend
 * attend listPrice + (discount OU choosePrice) ; celui fourni prime, l'autre est
 * dérivé. Parti pris : on expose le flux « choosePrice en entrée » (discount
 * dérivé) ; repasser sur « Discount (%) recommandé » suffirait à inverser le sens.
 */
const matchingKeywords = (keywords: string[]) => ({
  matching_keywords: keywords.join(', '),
  // CSVBox currently gates dynamic `matching_keywords` behind its unrelated
  // system-keywords option. Its regular `keywords` path is always evaluated.
  keywords: keywords.map((value) => ({ value })),
});

export const PRICE_COLUMNS = {
  choosePrice: {
    column_name: 'Prix choose ttc recommandé',
    required: false,
    ...matchingKeywords([
      'Prix Choose TTC',
      'Prix de vente psycho Choose',
      'Prix choose ttc recommandé',
      'Prix psychologiques',
      'Prix Choose',
      'Prix Choose TTC recommandé',
      'Choose Price',
      'Choose price (incl. VAT)',
      'Prix Choose TTC recommandé V2',
      'Prix choose proposé',
      'Prix Choose ttc',
      'Prix choose psychos',
      'Prix psychologique',
      'Prix choose ttc',
      'Prix Choose TTC Souvence',
      'Prix FINAL choose ttc',
      'Choose Price (€)  FINAL',
      'PRIX CHOOSE TTC',
      'Prix Choose hors FDP TTC',
      'Prix Choose OK',
      'Prix psychos',
      'Prix Choose x émoi émoi',
      'Prix psychologiques TTC',
      'Prix Psychologiques Négo Réduite',
      'Nouveaux Prix Choose',
      'Choose price (vat incl.)',
      'Prix Psychologiques',
      'Prix Psychos V2',
      'Prix Psychos',
      'Choose Price VAT included',
      'Prix psycho',
      'Prix choose TTC',
      'Prix psychologiques Choose',
      'Prix Choose V2',
      'Prix Choose \n recommandé TTC',
      'prix psycho',
      'Choose price (vat included)',
      'Choose Price TTC',
      'Choose price V2',
      'Choose price V2 (vat included)',
      'Choose Price, tax included',
      'Choose price, tax included',
      'Nouveau prix choose',
      'Nouvelle proposition',
      'prix chooose final',
      'PRIX CHOOSE',
      'Prix choose',
      'Prix Choose O-1',
      'Prix Choose TTC FDP inclus',
      'Prix Choose TTC psycho',
      'Prix choose TTC recommandé',
      'Prix choose ttc \nrecommandé',
      'Prix Choose TTC V1',
      'Prix choose ttc V3',
      'PRIX CHOOSE v2',
      'Prix choose V2',
      'prix choose V2',
      'Prix Psycho',
      'Prix Psycologique',
      'Prix remisé choose TTC psycho',
      'Psycho Prices',
      'Prix Choose TTC (€)',
      'Prix Choose TTC finaux',
      'Prixx Psychologique Choose',
      'Choose prices (V2)',
      'Prix Choose TTC recommandé \n Ne pas remplir, calcul automatique',
      'PRIX PSYCHO CHOOSER',
      'Prix choose recommande TTC',
      'Prix Choose TTC (proposition Eliot)',
      'PRIX CHOOSE PSYCHOLOGIQUES',
      'PRIX CHOOSE RECALCULÉS',
      'Prix choose last op',
      'Choose price',
      'CHOOSE PRICE PSYCHO',
      'Choose Prix Psycho',
      'Prix Choose Psycho ttc validés',
      'Prix Choose psychologique',
      'Prix Choose recommandé TTC',
      'Prix Choose recommandé ttc',
      'prix Choose TTC',
      'Prix choose ttc recommande',
      'PRIX FINAUX TTC',
      'PRIX PSYCHO CHOOSE',
      'Prix Psychologique',
      'PRIX PSYCHOS',
      'PRIX PSYCO',
      'Proposition prix psychologiques',
      'Prix de vente conseillé TTC CHOOSE',
      'Prix Retail TTC Choose',
      'Prix Choose Final',
      'Contre-proposition',
      'Contre proposition',
    ]),
  },
  retro: {
    column_name: 'Prix de rétrocession ht',
    required: false,
    ...matchingKeywords([
      'Prix de \nrétrocession HT',
      'Prix de rétrocession HT / Net amount payable',
      'Prix de rétrocession ht',
      'PRIX ACHAT EURO HT',
      'Prix de rétrocession HT',
      'Prix retro',
      'Buying Price',
      'Wholesale price (excl. VAT)',
      'Prix de rétrocession HT V2',
      "Prix d'achat",
      'Prix de rétrocession ht ajustés',
      'prix de rétrocession ht',
      'nouveau prix de rétrocession',
      'BtoB price FINAL',
      'PRIX RETROCESSION HT',
      "Prix d'achat HT (hors FDP et D3E)",
      "Prix d'achat FDP+D3E inclus HT",
      'Prix de rétro OK',
      'Nouveaux prix de rétro',
      "Prix d'achat HT",
      'Prix de retro V2',
      'PRIX ACHAT',
      'Prix de rétrocession',
      'Prix de \nrétrocession ht',
      'PRIX RETROCESSION HT hors DEEE',
      'Retrocession price VAT excluded',
      'Prix achat',
      'Prix de retrocession 15%',
      'Nouveau prix de rétro ht',
      'B2B PRICE - Furnichannel Choose\n(NET VAT)',
      'B2B price, tax excluded',
      'B2B purchase price',
      'B2B purchase price V3',
      'Btob price (vat excluded)',
      'BtoB Prices',
      'NEW Prix de rétro HT avec DEEE et FDP intégrés',
      'Nouveau prix de rétrocession',
      "Prix d'achat HT intégrant surplus FDP HT",
      "Prix d'achat HT/prix de rétro",
      'Prix de rétro HT',
      'prIX DE RETROCESSIOB',
      'PRIX DE RETROCESSION',
      'Prix de rétrocession FDP inclus HT',
      'Prix de rétrocession HT (€) psycho',
      'Prix de rétrocession HT (hors FDP et eco participation)',
      'Prix de rétrocession HT psycho',
      'Prix e retrocession',
      'PRIX RETROCESSION HT ADEVA',
      'PRIX RETROCESSION HT DEEE inclus',
      'Prix retrocession HT V3',
      'Purchase price Exclud TAX',
      'Rétrocession HT',
      'Sales Price WO VAT & W Sale (Btob price (vat excluded))',
      'Supplier Price Excl Tax',
      'Prix de rétrocession ht V2',
      'PRIX DE RÉTROCESSION HT',
      "PRIX D'ACHAT HT",
      'Prix de retro',
      "Prix d'achat HT finaux",
      'Btob price (vat excluded V2)',
      'Prix de rétrocessio HT',
      'Prix de retrocession HT',
      'Prix de rétro HT sans FDP',
      'Prix de rétro incluant FDP HT (colonne T) + Eco-part',
      'Delta HT à intégrer au prix de rétrocession',
      'Prix de rétrocession ht (prix B2B',
      'Nouveaux prix de retrocession HT 27082026',
      'Prix retrocession ht',
      'Buying price',
      'Btob price',
      'MAJ Prix de rétro HT',
      'PA HT',
      "Prix d'achat Choose HT",
      'Prix de retrocession ht',
      'Prix de rétrocession HT souhaités',
      'Prix de rétrocession HT validés',
      'Prix de rétrocession/achat HT',
      'PRIX RETROCESSION PSYCO HT',
      'RETROCESSION PRICE',
      "> prix d'achat Choose HT",
      'Prix de Rétrocession HT',
      'Prix de rétro',
      'B2B Price',
      'Prix B2B HT',
      'BtoB Price',
    ]),
  },
  margin: {
    column_name: 'Marge',
    required: false,
    ...matchingKeywords([
      'Marge Choose',
      'Marge',
      'Marge Choose %',
      'Choose margin',
      'Marge réelle',
      'Margin',
      'COM CHOOSE (%)',
      'Marge\nChoose',
      'Marge Choose hors FDP (HT)',
      'Marge réelle Choose',
      'MARGE CHOOSE (%)',
      'Commission',
      'marge',
      'MARGE CHOOSE',
      'Choose Margin',
      'MARGE',
      'Marge calculée',
      'Marge Choose (%)',
      'Marge réelle finale',
      'Marge réelle V1',
      'Margin ???',
      'Marge choose',
      'MARGIN',
      'Marge Choose souhaitée',
      'Marge choose (%)',
      'MARGIN CHOOSE (%)',
    ]),
  },
  discount: {
    column_name: 'Discount (%) recommandé',
    required: false,
    ...matchingKeywords([
      'Discount (%) recommandé',
      'Discount (%)',
      'Discount recommandé',
      'Discount Choose',
      'Discount',
      'Remise Choose',
      'Remise (%)',
      'Remise',
      '% de remise',
    ]),
  },
} satisfies Record<string, DynamicColumn>;

export const CSV_OFFER_PRICE_COLUMNS: DynamicColumn[] = [
  PRICE_COLUMNS.choosePrice,
  PRICE_COLUMNS.retro,
  PRICE_COLUMNS.margin,
  PRICE_COLUMNS.discount,
];

export const dynamicColumnsForPricingMode = (
  mode: PricingMode
): DynamicColumn[] => {
  const { choosePrice, retro, margin } = PRICE_COLUMNS;
  return mode === PricingMode.BuyingPriceFixed
    ? [choosePrice, retro]
    : [choosePrice, margin];
};

/** Nettoie un nom de fichier : retire accents et caractères non sûrs. */
export const sanitizeFilename = (filename: string) => {
  return filename
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Supprimer les accents
    .replace(/[^\w\s.-]/g, ''); // Garder seulement alphanumérique, espaces, points et tirets
};

/**
 * Modes d'import du flux CSV produits (CSVBox) : `CreateOnly` / `UpdateOnly`
 * (flux de production « Ajouter / Mettre à jour »), `SyncWithDealItemId`
 * (Option + clic, internes Choose).
 */
export type CsvImportInstructionMode =
  | DatasourceInstructionMode.SyncWithDealItemId
  | DatasourceInstructionMode.CreateOnly
  | DatasourceInstructionMode.UpdateOnly;

/** Libellé court du mode, envoyé à CSVBox (custom field) et à l'analytics. */
export type CsvImportModeLabel = 'sync' | 'add' | 'update';

export const csvImportModeLabel = (
  mode: CsvImportInstructionMode
): CsvImportModeLabel => {
  switch (mode) {
    case DatasourceInstructionMode.SyncWithDealItemId:
      return 'sync';
    case DatasourceInstructionMode.UpdateOnly:
      return 'update';
    default:
      return 'add';
  }
};

/**
 * En-tête de la colonne ID deal item attendu par le backend (cf.
 * `TranslationsUseCase` : `Deal Item Id (ne pas supprimer)` / `Deal Item Id (do
 * not delete)`, comparaison exacte après minuscules et trim).
 */
export const DEAL_ITEM_ID_HEADER = 'Deal Item Id (ne pas supprimer)';

/** Variantes rencontrées (column_name CSVBox, export, saisie manuelle). */
const DEAL_ITEM_ID_ALIASES = new Set([
  'deal item id (ne pas supprimer)',
  'deal item id (do not delete)',
  'deal item id',
  'id deal item',
  'dealitemid',
  'deal_item_id',
  'deal-item-id',
]);

export const isDealItemIdHeader = (header: string) =>
  DEAL_ITEM_ID_ALIASES.has(header.trim().toLowerCase());

/**
 * Remonte la colonne ID deal item sous l'en-tête que le backend reconnaît.
 *
 * Seule une colonne mappée dans CSVBox est prise en compte : CSVBox la renvoie
 * sous son `column_name` (`Deal Item Id (ne pas supprimer)`, ou un alias d'un
 * ancien sheet) et on
 * la renomme si besoin. Une colonne d'id laissée non mappée reste dans
 * `_unmapped_data` et n'est jamais lue : le mapping CSVBox fait foi, le backend
 * ne reçoit alors aucun id. Les autres colonnes sont inchangées.
 */
export function normalizeDealItemIdColumn(rows: Record<string, unknown>[]) {
  return rows.map((row) => {
    const mappedKey = Object.keys(row).find(isDealItemIdHeader);
    if (!mappedKey || mappedKey === DEAL_ITEM_ID_HEADER) {
      return row;
    }
    const { [mappedKey]: value, ...rest } = row;
    return { ...rest, [DEAL_ITEM_ID_HEADER]: value };
  });
}

/**
 * Retire la colonne ID deal item des lignes (flow legacy « Ajouter / Mettre à
 * jour ») : le backend ne la lit qu'en synchronisation et, sans liste E-Merch
 * explicite, range toute colonne non consommée en E-Merch. Envoyée telle
 * quelle, elle deviendrait un champ E-Merch « Deal Item Id » sur chaque
 * produit.
 */
export function dropDealItemIdColumn(rows: Record<string, unknown>[]) {
  return rows.map((row) =>
    Object.fromEntries(
      Object.entries(row).filter(([key]) => !isDealItemIdHeader(key))
    )
  );
}
